import { Box, Card } from 'src/components/ui';
import { ICreative } from 'src/lib/schemas/campaign/newFlowCampaign';
import CardActions from './CardActions';
import useToggleWithPayload from 'src/hooks/useToggleWithPayload';
import { useContext, useState, useEffect } from 'react';
import { CampaignContext } from 'src/contexts';
import {
	getCampaign,
	regenerateCampaignCreative,
	updateCampaignCreative,
} from 'src/services/campaign';
import { toastError, toastSuccess } from 'src/services/toast';
import { IChannelCreativeAttributes } from 'src/lib/schemas';
import Design from 'src/pages/design/Design';
import EditCreativeModal from './EditCreativeModal';
import { TOP_BAR_HEIGHT, validTextInputKeys } from '../utils/constants';

interface CreativeCardProps {
	creative: ICreative;
	withActions?: boolean;
	size?: string;
	children: React.ReactNode;
	title: string;
	creativesConfig?: IChannelCreativeAttributes[];
	onCopy?: () => void;
}

const CreativeCard = ({
	creative,
	withActions = true,
	size = 'md',
	children,
	title,
	creativesConfig,
	onCopy,
}: CreativeCardProps) => {
	const creativeModalToggle = useToggleWithPayload<ICreative>(creative);
	const { id: campaignId, campaign, setCampaign, setIsEditorMode } = useContext(CampaignContext);
	const [editingCreative, setEditingCreative] = useState<ICreative | null>(
		null,
	);
	// Find config by matching channel - handle different naming conventions
	const config = creativesConfig?.find((c) => {
		const channelLower = creative.channel?.toLowerCase() || '';
		const configIdLower = c.id?.toLowerCase() || '';
		
		// Direct match
		if (configIdLower === channelLower) return true;
		
		// Handle meta/facebook variations
		if (channelLower.includes('facebook') || channelLower.includes('meta')) {
			return configIdLower === 'facebook' || configIdLower === 'meta';
		}
		if (channelLower.includes('instagram')) {
			return configIdLower === 'instagram' || configIdLower === 'meta';
		}
		if (channelLower.includes('google')) {
			return configIdLower === 'google';
		}
		
		// Partial match as fallback
		return configIdLower.includes(channelLower) || channelLower.includes(configIdLower);
	});
	const withImage = creative.adEngine === 'creatomate';

	// Manage editor mode state when editing creative
	useEffect(() => {
		if (editingCreative?.attributes && Array.isArray((editingCreative as any).attributes.elements)) {
			setIsEditorMode(true); // Enable editor mode when opening Design editor
		} else {
			setIsEditorMode(false); // Disable editor mode when closing
		}
	}, [editingCreative, setIsEditorMode]);

	const handleRefetchCreatives = async () => {
		if (!campaign?.id) return;
		try {
			const response = await getCampaign(campaign.id);
			setCampaign(response);
		} catch (error: any) {
			toastError(error.message || 'Failed to fetch creatives');
		}
	};

	const handleRegenerateCreative = async () => {
		if (!campaign?.id) return;
		try {
			await regenerateCampaignCreative(campaign.id, {
				creativeId: creative.id,
			});
			const response = await getCampaign(campaign.id);
			setCampaign(response);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleOpenAdvancedEditor = () => {
		setEditingCreative(creative);
	};

	const handleOpenSimpleEditor = () => {
		creativeModalToggle.onOpen(creative);
	};

	const handleEditCreative = () => {
		const fields = (creative as any).attributes.output ?? creative.attributes;
		const textFields = Object.keys(fields).filter((key) => {
			return validTextInputKeys.includes(key);
		});
		if (!textFields.length) {
			handleOpenAdvancedEditor();
		} else {
			handleOpenSimpleEditor();
		}
	};

	const handleClose = () => {
		setEditingCreative(null);
	};

	const handleSave = async (updatedTemplate: any) => {
		if (!campaign?.id || !editingCreative) return;

		const updatedPayload = { attributes: updatedTemplate };

		await updateCampaignCreative(
			campaign.id,
			editingCreative.id,
			updatedPayload,
		);

		handleRefetchCreatives();
		handleClose();
		toastSuccess('Your changes have been saved');
	};

	const handleChange = (updatedTemplate: any) => {
		setEditingCreative((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				attributes: { ...prev.attributes, elements: updatedTemplate.elements },
			};
		});
	};

	if (
		editingCreative?.attributes &&
		Array.isArray((editingCreative as any).attributes.elements)
	) {
		return (
			<Box
				position="fixed"
				top={TOP_BAR_HEIGHT}
				left="0"
				right="0"
				bottom="0"
				zIndex="overlay"
				bg="white"
				overflow="hidden"
			>
				<Design
					template={editingCreative.attributes}
					onClose={handleClose}
					onSave={handleSave}
					onChange={handleChange}
					fonts={[]}
				/>
			</Box>
		);
	}

	return (
		<>
			<Box
				w={{ base: '100%', sm: 'auto' }}
				maxW={{ base: 'calc(100vw - 32px)', sm: size }}
			>
				{withActions && (
					<CardActions
						title={title}
						onEdit={handleEditCreative}
						onRegenerate={handleRegenerateCreative}
						onCopy={onCopy}
						mb="10px"
					/>
				)}
				<Card
					border="1px"
					borderRadius="lg"
					borderColor="gray.200"
					bg="white"
					boxShadow="md"
				>
					{children}
				</Card>
			</Box>
			{creativeModalToggle.payload && creativeModalToggle.isOpen && (
				<EditCreativeModal
					isOpen
					onClose={creativeModalToggle.onClose}
					initialValues={creativeModalToggle.payload}
					config={config}
					handleRefetchCreatives={handleRefetchCreatives}
				/>
			)}
		</>
	);
};

export default CreativeCard;
